import { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Counts from 0 up to `target` with an ease-out curve, restarting whenever `restartKey` changes.
 * Jumps straight to the target when reduced motion is requested or the tab is hidden, and when
 * the target changes without a restart (e.g. a recalculated total).
 */
export function useCountUp(target: number, restartKey: unknown, duration = 800): number {
  const [value, setValue] = useState(0);
  const lastKey = useRef<unknown>(Symbol('unset'));

  useEffect(() => {
    const restart = !Object.is(lastKey.current, restartKey);
    lastKey.current = restartKey;
    if (!restart || !Number.isFinite(target) || prefersReducedMotion() || (typeof document !== 'undefined' && document.hidden)) {
      setValue(target);
      return undefined;
    }
    let frame = 0;
    let done = false;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      setValue(target * (1 - Math.pow(1 - t, 3)));
      if (t < 1) frame = requestAnimationFrame(tick);
      else done = true;
    };
    setValue(0);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      // Interrupted (StrictMode remount, or the target moved mid-count): let the next run start over.
      if (!done) lastKey.current = Symbol('unset');
    };
  }, [restartKey, target, duration]);

  return value;
}
